// Нормализация ввода клиентов: телефон в E.164, почта в нижнем регистре с доменом в Unicode, ИНН.
// Чистые функции без внешних пакетов — их зовут и сервер, и браузер (поиск совпадений при вводе).

/** Невидимые символы, которые приходят со вставкой из мессенджеров и документов. */
const INVISIBLE = /[\u00a0\u200b-\u200d\u2060\ufeff]/g;

/** Текст без лишних пробелов; пустой — null. */
export function normalizeText(value: string | null | undefined): string | null {
  const clean = (value ?? '').replace(INVISIBLE, ' ').trim().replace(/\s+/g, ' ');
  return clean === '' ? null : clean;
}

// Punycode (RFC 3492): домен `xn--p1ai` показывается и хранится как `рф`.
const BASE = 36;
const T_MIN = 1;
const T_MAX = 26;
const SKEW = 38;
const DAMP = 700;
const INITIAL_BIAS = 72;
const INITIAL_N = 128;

function adapt(delta: number, points: number, first: boolean): number {
  let d = first ? Math.floor(delta / DAMP) : delta >> 1;
  d += Math.floor(d / points);
  let k = 0;
  while (d > ((BASE - T_MIN) * T_MAX) >> 1) {
    d = Math.floor(d / (BASE - T_MIN));
    k += BASE;
  }
  return k + Math.floor(((BASE - T_MIN + 1) * d) / (d + SKEW));
}

function digitOf(code: number): number {
  if (code >= 0x30 && code <= 0x39) return code - 0x30 + 26;
  if (code >= 0x41 && code <= 0x5a) return code - 0x41;
  if (code >= 0x61 && code <= 0x7a) return code - 0x61;
  return BASE;
}

/** Метка домена из punycode; нераспознанная возвращается как есть. */
function decodeLabel(label: string): string {
  const input = label.slice(4);
  const basicEnd = Math.max(input.lastIndexOf('-'), 0);
  const output: number[] = [];
  for (let j = 0; j < basicEnd; j++) {
    const code = input.charCodeAt(j);
    if (code >= 0x80) return label;
    output.push(code);
  }
  let n = INITIAL_N;
  let i = 0;
  let bias = INITIAL_BIAS;
  for (let index = basicEnd > 0 ? basicEnd + 1 : 0; index < input.length;) {
    const previous = i;
    for (let w = 1, k = BASE; ; k += BASE) {
      if (index >= input.length) return label;
      const digit = digitOf(input.charCodeAt(index++));
      if (digit >= BASE) return label;
      i += digit * w;
      const threshold = k <= bias ? T_MIN : k >= bias + T_MAX ? T_MAX : k - bias;
      if (digit < threshold) break;
      w *= BASE - threshold;
    }
    const length = output.length + 1;
    bias = adapt(i - previous, length, previous === 0);
    n += Math.floor(i / length);
    if (n > 0x10ffff) return label;
    i %= length;
    output.splice(i++, 0, n);
  }
  return String.fromCodePoint(...output);
}

/** Домен в Unicode: `xn--e1afmkfd.xn--p1ai` → `пример.рф`, регистр и форма записи приведены к одному виду. */
export function domainToUnicode(domain: string): string {
  return domain
    .replace(/[\u3002\uff0e\uff61]/g, '.')
    .toLowerCase()
    .split('.')
    .map((label) => (label.startsWith('xn--') ? decodeLabel(label) : label))
    .join('.')
    .normalize('NFC');
}

// Домен из меток букв и цифр (включая кириллицу — адреса EAI), зона — от двух букв.
const EMAIL = /^[^\s@]+@(?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+\p{L}{2,}$/u;
const EMAIL_MAX = 254;
const EMAIL_LOCAL_MAX = 64;

/**
 * Почта для хранения и сравнения: без невидимых символов, в нижнем регистре, домен в Unicode.
 * Не похоже на адрес — null.
 */
export function normalizeEmail(input: string): string | null {
  const clean = input.replace(INVISIBLE, '').trim();
  const at = clean.lastIndexOf('@');
  if (at <= 0) return null;
  const local = clean.slice(0, at).normalize('NFC').toLowerCase();
  const email = `${local}@${domainToUnicode(clean.slice(at + 1))}`;
  if (email.length > EMAIL_MAX || local.length > EMAIL_LOCAL_MAX) return null;
  return EMAIL.test(email) ? email : null;
}

/** Добавочный номер («доб. 123», «ext 5», «#7») не входит в номер для сравнения. */
const EXTENSION = /\s*(?:доб(?:авочный)?\.?|ext\.?|вн\.?|#)\s*\d+\s*$/iu;

/**
 * Телефон в E.164: `8 (999) 123-45-67` → `+79991234567`. Без кода страны номер российский;
 * иностранный вводится с «+» или «00». Не похоже на номер — null.
 */
export function normalizePhone(input: string): string | null {
  const text = input.replace(INVISIBLE, ' ').replace(EXTENSION, '').trim();
  const digits = text.replace(/\D/g, '');
  if (digits === '') return null;
  let international: string;
  if (text.startsWith('+')) international = digits;
  else if (digits.startsWith('00')) international = digits.slice(2);
  else if (digits.length === 11 && (digits.startsWith('8') || digits.startsWith('7')))
    international = `7${digits.slice(1)}`;
  else if (digits.length === 10) international = `7${digits}`;
  else return null;
  if (international.startsWith('7')) return international.length === 11 ? `+${international}` : null;
  if (international.startsWith('0') || international.length < 8 || international.length > 15) return null;
  return `+${international}`;
}

/** ИНН как храним: только цифры, без пробелов; пустой — null (правильность проверяет `clientIssues`). */
export function normalizeInn(input: string | null | undefined): string | null {
  const value = (input ?? '').replace(/\s/g, '');
  return value === '' ? null : value;
}
